/** AI token usage for admins. A super-admin sees per-tenant totals across the
 *  platform; a tenant-admin sees their own learners' usage this month. Both
 *  read from the same page — the role picks the data source and title. */
import { useEffect, useState } from "react";
import { platformTenantUsage, tenantLearnerUsage } from "../../api/aiUsage";
import { useAuth } from "../../auth/AuthContext";
import { Card, EmptyState, Spinner } from "../../components/ui";
import { useT } from "../../i18n";

interface Row {
  key: string;
  name: string;
  sub: string | null;
  tokens: number;
}

export function AiUsagePage() {
  const t = useT();
  const { isSuperAdmin } = useAuth();
  const [rows, setRows] = useState<Row[] | null>(null);

  useEffect(() => {
    const load = isSuperAdmin
      ? platformTenantUsage().then((ts) =>
          ts.map((r) => ({ key: r.tenant_id, name: r.name, sub: null, tokens: r.tokens_this_month })),
        )
      : tenantLearnerUsage().then((ls) =>
          ls.map((r) => ({ key: r.user_id, name: r.full_name, sub: r.email, tokens: r.tokens_this_month })),
        );
    load.then(setRows).catch(() => setRows([]));
  }, [isSuperAdmin]);

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-extrabold">
        {t(isSuperAdmin ? "usage.tenants" : "usage.learners")}
      </h1>
      <Card>
        {rows === null ? (
          <div className="grid place-items-center py-8">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState message={t("usage.none")} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase text-muted">
                  <th className="py-2 font-semibold">{t("usage.name")}</th>
                  <th className="py-2 text-right font-semibold">{t("usage.tokensThisMonth")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b border-line/50">
                    <td className="py-2">
                      <div className="font-semibold text-ink">{r.name}</div>
                      {r.sub && <div className="text-xs text-muted">{r.sub}</div>}
                    </td>
                    <td className="py-2 text-right tabular-nums text-muted">
                      {r.tokens.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
